import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Trophy, Users, Play } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const WS_URL = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');

const Home = () => {
  const navigate = useNavigate();
  const [playerName, setPlayerName] = useState("");
  const [playerId] = useState(() => Math.random().toString(36).substring(2, 11));
  const [connectedPlayers, setConnectedPlayers] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  const wsRef = useRef(null);

  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  const connectToLobby = () => {
    if (!playerName.trim()) return;

    const ws = new WebSocket(`${WS_URL}/api/ws/${playerId}`);
    wsRef.current = ws;

    ws.onopen = () => {
      ws.send(JSON.stringify({ player_name: playerName }));
      setIsConnected(true);
    };

    ws.onmessage = (event) => {
      const message = JSON.parse(event.data);

      if (message.type === "game_state") {
        const players = Object.entries(message.data.players).map(([id, data]) => ({
          id,
          name: data.name,
          progress: data.progress
        }));
        setConnectedPlayers(players);
      }

      if (message.type === "race_started") {
        setGameStarted(true);
        // Close the WebSocket before navigation
        if (wsRef.current) {
          wsRef.current.close();
        }
        setTimeout(() => {
          navigate("/game", { state: { playerId, playerName } });
        }, 1000);
      }
    };

    ws.onerror = (error) => {
      console.error("WebSocket error:", error);
    };
  };

  const startRace = () => {
    if (wsRef.current && connectedPlayers.length > 0) {
      wsRef.current.send(JSON.stringify({ type: "start_game" }));
    }
  };

  return (
    <div 
      className="min-h-screen flex flex-col items-center justify-center p-8"
      style={{
        backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/1cd2d0b9-b28c-4677-afeb-8f22de75f592/images/111801b05631de02d76cfb120c1a967e6f44e0d9d6c0752c9a9ac84ea38aed7d.png')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <div className="max-w-4xl w-full">
        {/* Title */}
        <div className="text-center mb-12">
          <h1 
            className="text-5xl sm:text-6xl tracking-tighter uppercase mb-4 text-[#121212]"
            style={{ fontFamily: 'Fredoka, sans-serif', fontWeight: 900 }}
            data-testid="game-title"
          >
            KERN CYCLE RACER
          </h1>
          <p className="text-lg font-bold text-[#333333]" data-testid="game-subtitle">
            Race to arrange the curriculum design cycle!
          </p>
        </div>

        {!isConnected ? (
          /* Join Form */
          <div 
            className="bg-[#FFFBEB] border-4 border-[#121212] p-8 neo-shadow max-w-md mx-auto"
            data-testid="join-form"
          >
            <h2 
              className="text-3xl sm:text-4xl tracking-tight uppercase mb-6 text-[#121212]"
              style={{ fontFamily: 'Fredoka, sans-serif', fontWeight: 700 }}
            >
              JOIN LOBBY
            </h2>
            
            <input
              type="text"
              placeholder="Enter your name"
              value={playerName}
              onChange={(e) => setPlayerName(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && connectToLobby()}
              className="w-full px-4 py-3 border-4 border-[#121212] text-lg font-bold mb-6 focus:outline-none focus:ring-4 focus:ring-[#FFD500]"
              maxLength={20}
              data-testid="player-name-input"
            />

            <button
              onClick={connectToLobby}
              disabled={!playerName.trim()}
              className="w-full bg-[#FFD500] border-4 border-[#121212] px-6 py-4 text-xl font-black uppercase neo-shadow neo-button transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ fontFamily: 'Fredoka, sans-serif' }}
              data-testid="join-lobby-button"
            >
              <Users className="inline mr-2" strokeWidth={3} />
              Join Lobby
            </button>

            <button
              onClick={() => navigate('/leaderboard')}
              className="w-full mt-4 bg-[#0055FF] text-white border-4 border-[#121212] px-6 py-3 text-lg font-bold uppercase neo-shadow neo-button transition-all"
              style={{ fontFamily: 'Fredoka, sans-serif' }}
              data-testid="view-leaderboard-button"
            >
              <Trophy className="inline mr-2" strokeWidth={3} />
              Leaderboard
            </button>
          </div>
        ) : (
          /* Lobby */
          <div 
            className="bg-[#FFFBEB] border-4 border-[#121212] p-8 neo-shadow"
            data-testid="lobby-screen"
          >
            <h2 
              className="text-3xl sm:text-4xl tracking-tight uppercase mb-6 text-[#121212]"
              style={{ fontFamily: 'Fredoka, sans-serif', fontWeight: 700 }}
            >
              WAITING ROOM
            </h2>

            <div className="mb-6">
              <p className="text-sm uppercase tracking-[0.15em] mb-3 font-bold text-[#333333]">
                Players ({connectedPlayers.length}/8)
              </p>
              <div className="space-y-2">
                {connectedPlayers.map((player, idx) => (
                  <div
                    key={player.id}
                    className="bg-white border-4 border-[#121212] p-4 flex items-center justify-between"
                    data-testid={`player-${idx}`}
                  >
                    <span className="font-bold text-lg">{player.name}</span>
                    {player.id === playerId && (
                      <span className="bg-[#FFD500] px-3 py-1 border-2 border-[#121212] text-xs font-black uppercase">
                        YOU
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {connectedPlayers.length > 0 && (
              <button
                onClick={startRace}
                disabled={gameStarted}
                className="w-full bg-[#00E676] border-4 border-[#121212] px-6 py-4 text-xl font-black uppercase neo-shadow neo-button transition-all disabled:opacity-50"
                style={{ fontFamily: 'Fredoka, sans-serif' }}
                data-testid="start-race-button"
              >
                <Play className="inline mr-2" strokeWidth={3} />
                {gameStarted ? "STARTING..." : "START RACE"}
              </button>
            )}

            <p className="mt-4 text-center text-sm text-[#333333] font-bold">
              {connectedPlayers.length < 8 ? "Waiting for players..." : "Lobby Full!"}
            </p>
          </div>
        )}

        {/* How to Play */}
        <div className="mt-8 bg-white border-4 border-[#121212] p-6 neo-shadow" data-testid="how-to-play">
          <h3 
            className="text-xl sm:text-2xl mb-4 text-[#121212] uppercase"
            style={{ fontFamily: 'Fredoka, sans-serif', fontWeight: 700 }}
          >
            HOW TO PLAY
          </h3>
          <ol className="list-decimal list-inside space-y-2 text-base font-bold text-[#333333]">
            <li>Drag and drop the 6 Kern Cycle elements into the correct order</li>
            <li>You can start at any point, but must proceed in sequence</li>
            <li>Get visual feedback for correct and incorrect placements</li>
            <li>Complete the cycle in the shortest time to top the leaderboard!</li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default Home;
